import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { captureMessage } from "@sentry/browser";
import { isNil } from "lodash";
import queryString from "query-string";
import { useHistory } from "react-router";
import { z } from "zod";
import {
  buildLink,
  INFO_PANEL_TAB_NAME,
  RECORDS_TAB_NAME,
} from "common/routing";
import { arrayHasAtLeastOneItem } from "common/utils/arrays";
import { SearchResultProps } from "common-client/utils/search";
import { MapsForAccountQuery } from "../../generated/graphql";
import { useLoadMapIcons } from "../../hooks/useMapIcons";
import useURLParsing from "../../hooks/useURLParsing";
import { isSmall } from "../../utils/size";
import { track } from "../../utils/tracking";
import AddressPanel from "../AddressPanel";
import { AuthContext } from "../Authorization/AuthContext";
import ObjectInformationPanel from "../ObjectInformationPanel";
import { SearchContext } from "../Search/SearchContext";
import { Container } from "./__styles__/FullPageMap";
import { Info } from "./__styles__/Map";
import FullPageMap from "./FullPageMap";
import { InternalMapContext } from "./InternalMapContextProvider";
import { ClickObjectMenuLayerConfig } from "./LayeredMap/clickObjectMenuLayer";
import {
  CustomMapLayerConfig,
  CustomMapLayerValue,
} from "./LayeredMap/customMapLayer";
import { MarkerLayerConfig } from "./LayeredMap/markerLayer";
import { ParcelLayerConfig } from "./LayeredMap/parcelLayer";
import { PropertyLayerConfig } from "./LayeredMap/propertyLayer";
import { SavedViewsLayerConfig } from "./LayeredMap/savedViewsLayer";
import { Property } from "./LayeredMap/types";
import { LayerContext } from "./layers";
import { useUserDeviceLocations } from "./UserDeviceLocation/useUserDeviceLocations";

export const selectedSchema = z.union([
  z
    .object({})
    .strict()
    .transform(() => null),
  z
    .object({
      customMapId: z.uuid(),
      geometryId: z.uuid(),
      fromSavedView: z
        .string()
        .optional()
        .transform(val => (val === undefined ? undefined : val === "true")),
      tab: z.enum(INFO_PANEL_TAB_NAME).optional(),
      recordTab: z.enum(RECORDS_TAB_NAME).optional(),
    })
    .strict()
    .transform(props => ({ ...props, type: "geometry" as const })),
  z
    .object({
      lat: z.coerce.number<string | number>(),
      lng: z.coerce.number<string | number>(),
      address: z.string().nullish(),
      propertyId: z.uuid().nullish(),
      parcelId: z.uuid().nullish(),
      parcelNumber: z.string().nullish(),
      hasPropertyIds: z
        .string()
        .nullish()
        .transform(value => value === "true"),
      tab: z.enum(INFO_PANEL_TAB_NAME).optional(),
      recordTab: z.enum(RECORDS_TAB_NAME).optional(),
    })
    .strict()
    .transform(({ lat, lng, ...rest }) => ({
      type: "address" as const,
      latitude: lat,
      longitude: lng,
      ...rest,
    })),
]);

export const isProperty = (obj: any): obj is Property => {
  return (
    obj &&
    typeof obj === "object" &&
    typeof obj.id === "string" &&
    "address" in obj &&
    "point" in obj
  );
};

export const isCustomMap = (obj: any): obj is CustomMapLayerValue => {
  return (
    obj &&
    typeof obj === "object" &&
    typeof obj.customMapId === "string" &&
    typeof obj.geometryId === "string"
  );
};

export type SelectedInput = z.input<typeof selectedSchema>;
type Selected = z.infer<typeof selectedSchema>;

export const InternalMap = ({
  account,
}: {
  account: NonNullable<MapsForAccountQuery["account"]>;
}) => {
  const history = useHistory();
  const [selected, setSelected] = useState<Maybe<Selected>>(null);
  const { resetSearchCategory } = useContext(InternalMapContext);
  const {
    measureToolDispatch,
    measureToolState,
    visibleRaster,
    visibleSavedViews: getVisibleSavedViews,
    toggleLayer,
    approximateBfeToolDispatch,
    approximateBfeToolState,
    pointInGeometry,
    setGeometryBounds,
    setPointInGeometry,
  } = useContext(LayerContext);
  const { searchQueryState } = useContext(SearchContext);

  const loadMapIcons = useLoadMapIcons();

  const { account: authAccount } = useContext(AuthContext);
  const customMaps = authAccount?.customMaps ?? [];

  const userDeviceLocations = useUserDeviceLocations();

  // we don't want the address panel to re-render every time *this* re-renders,
  // so we  memoize this callback so that creating it happens only once
  const onAddressPanelClose = useMemo(
    () => () => {
      if (
        measureToolState.measureToolMode === "raster" &&
        measureToolState.dataMode === "store"
      ) {
        measureToolDispatch({
          type: "setMeasureMode",
          data: {
            measureToolMode: "off",
          },
        });

        toggleLayer({
          group: "rasters",
          id: visibleRaster()?.id,
          isVisible: false,
        });
      }
      if (approximateBfeToolState?.mode !== "off") {
        approximateBfeToolDispatch?.({
          type: "setMode",
          data: {
            mode: "off",
          },
        });
      }

      // clear out customMap related state
      setGeometryBounds(null);
      setPointInGeometry(null);

      history.replace({ pathname: buildLink("map") });
    },
    [
      history,
      measureToolState.measureToolMode,
      measureToolState.dataMode,
      measureToolDispatch,
      approximateBfeToolState?.mode,
      approximateBfeToolDispatch,
      setGeometryBounds,
      setPointInGeometry,
      toggleLayer,
      visibleRaster,
    ],
  );

  const updateURL = useCallback(
    ({
      queryParameters,
      fromSearch = false,
    }: {
      queryParameters: Record<string, string | number | null>;
      fromSearch?: boolean;
    }) => {
      history.replace({
        pathname: buildLink("map"),
        search: `?${queryString.stringify(queryParameters)}`,
        state: { fromSearch },
      });
    },
    [history],
  );

  const onParcelClick: ParcelLayerConfig["onClick"] = parcels => {
    // we should have exactly one parcel
    // because of the disambiguation menu layer
    if (!arrayHasAtLeastOneItem(parcels)) {
      captureMessage("Internal map clicked on zero parcels");
      return;
    } else if (parcels.length > 1) {
      captureMessage("Internal map clicked on multiple parcels", {
        extra: {
          parcels,
        },
      });
    }
    const parcel = parcels[0];

    // clear out customMap related state
    setGeometryBounds(null);
    setPointInGeometry(null);

    const tab =
      (selected?.type === "address" && selected.tab) ||
      INFO_PANEL_TAB_NAME.OVERVIEW;
    const { fullAddress, id, parcelNumber, propertyIds } = parcel;
    const { longitude, latitude } = parcel.point;
    const hasPropertyIds = propertyIds.length ? "true" : "false";

    const queryParameters = {
      address: fullAddress ? fullAddress : null,
      lng: longitude,
      lat: latitude,
      tab,
      parcelId: id,
      parcelNumber,
      hasPropertyIds,
    } satisfies SelectedInput;

    navigateToProperty(queryParameters);
  };

  const getAddressQueryParam = (property: Property) => {
    const { address, city, state, zipcode } = property;

    if (isNil(address) || isNil(city) || isNil(state) || isNil(zipcode)) {
      return undefined;
    }

    return `${address}, ${city}, ${state} ${zipcode}`;
  };

  const onFeatureClick = ({
    property,
    defaultTab,
  }: {
    property: Property;
    defaultTab: INFO_PANEL_TAB_NAME;
  }) => {
    // clear out customMap related state
    setGeometryBounds(null);
    setPointInGeometry(null);

    const address = getAddressQueryParam(property);

    const { point, id, parcel } = property;
    const { longitude, latitude } = point;

    const queryParameters = {
      lng: longitude,
      lat: latitude,
      propertyId: id,
      tab: defaultTab,
      ...(address ? { address } : {}),
      ...(parcel ?
        { parcelId: parcel.id, parcelNumber: parcel.parcelNumber }
      : {}),
    } satisfies SelectedInput;

    navigateToProperty(queryParameters);
  };

  const onPropertyClick: PropertyLayerConfig["onClick"] = property => {
    const defaultTab =
      (selected?.type === "address" && selected.tab) ||
      INFO_PANEL_TAB_NAME.OVERVIEW;

    onFeatureClick({
      property,
      defaultTab,
    });
  };

  const onSavedViewClick: SavedViewsLayerConfig["onClick"] = object => {
    if (isProperty(object)) {
      onPropertyClick(object);
    } else if (isCustomMap(object)) {
      navigateToCustomMapGeometry(object);
    }
  };

  const navigateToProperty = (
    queryParameters: SelectedInput & { tab?: string },
  ) => {
    track("Property clicked");
    resetSearchCategory();
    updateURL({ queryParameters });
  };

  const navigateToCustomMapGeometry = (
    queryParameters: Record<string, Maybe<string | number>>,
  ) => {
    track("Custom Map Geometry clicked");
    resetSearchCategory();
    updateURL({ queryParameters });
  };

  const handleSearchResultClick = useCallback(
    (data: SearchResultProps) => {
      // clear out customMap related state
      setGeometryBounds(null);
      setPointInGeometry(null);

      const { longitude, latitude } = data.point;
      const address = data.address;
      const propertyId = data.propertyId;
      const parcelId = data.geocacheParcelId;

      track("Address Searched");

      const queryParameters = {
        address,
        lng: longitude,
        lat: latitude,
        propertyId: propertyId || null,
        parcelId: parcelId || null,
        tab: INFO_PANEL_TAB_NAME.OVERVIEW,
      } satisfies SelectedInput;

      updateURL({ queryParameters, fromSearch: true });
    },
    [setGeometryBounds, setPointInGeometry, updateURL],
  );

  const parsedSchema = useURLParsing({
    routeName: "map",
    parser: selectedSchema,
  });

  useEffect(() => {
    setSelected(parsedSchema);
  }, [parsedSchema]);

  const ADDRESS_PANEL_WIDTH = 548;

  const viewportDimensions = () => {
    const width = window.innerWidth;
    const height = window.innerHeight;

    let newWidth;
    let newHeight;

    if (isSmall()) {
      // On mobile, we center the area width-wise and place it near the top
      // heigth-wise so that it sits above the AddressPanel
      newHeight = height - height / 5;
      newWidth = width / 2;
    } else {
      // On desktop, we center it width-wise taking into account the width of the
      // AddressPanel and center it heighth-wise
      newHeight = height / 2;
      newWidth = (width - ADDRESS_PANEL_WIDTH) / 2;
    }

    return { width: newWidth, height: newHeight };
  };

  const markerValue = useMemo(() => {
    if (searchQueryState.searchPoint) {
      return searchQueryState.searchPoint;
    }
    if (selected?.type === "address") {
      return { latitude: selected.latitude, longitude: selected.longitude };
    }
    if (selected?.type === "geometry") {
      return pointInGeometry;
    }
    return null;
  }, [searchQueryState.searchPoint, selected, pointInGeometry]);

  const markerConfig = {
    value: markerValue,
  } satisfies MarkerLayerConfig;

  const parcelConfig = {
    interactive: { hover: true, click: true },
    onClick: onParcelClick,
    value:
      selected?.type === "address" ?
        {
          id: selected.parcelId,
          point: {
            latitude: selected.latitude,
            longitude: selected.longitude,
          },
        }
      : null,
  } satisfies ParcelLayerConfig;

  const propertyConfig = {
    geocacheParcelId:
      selected?.type === "address" ? selected.parcelId : undefined,
    interactive: { hover: true, click: true },
    onClick: onPropertyClick,
  } satisfies PropertyLayerConfig;

  const customMapsConfig = {
    interactive: { click: true, hover: true },
    maps: customMaps,
    value: {
      customMapId:
        selected?.type === "geometry" ? selected.customMapId : undefined,
      geometryId:
        selected?.type === "geometry" ? selected.geometryId : undefined,
    },
    onClick: navigateToCustomMapGeometry,
  } satisfies CustomMapLayerConfig;

  const visibleSavedViews = getVisibleSavedViews();

  const savedViewsConfig = {
    onClick: onSavedViewClick,
    value: visibleSavedViews,
    interactive: {
      hover: !!visibleSavedViews?.length,
      click: !!visibleSavedViews?.length,
    },
    savedViews: account.savedViews,
  } satisfies SavedViewsLayerConfig;

  const clickObjectMenuConfig = {
    interactive: { click: true, hover: true },
    maps: customMapsConfig.maps,
    savedViews: savedViewsConfig.value,
  } satisfies ClickObjectMenuLayerConfig;

  return (
    <Container>
      <FullPageMap
        account={account}
        markerConfig={markerConfig}
        parcelConfig={parcelConfig}
        propertyConfig={propertyConfig}
        customMapsConfig={customMapsConfig}
        savedViewsConfig={savedViewsConfig}
        deviceLocationsConfig={userDeviceLocations}
        clickObjectMenuConfig={clickObjectMenuConfig}
        handleSearchResultClick={handleSearchResultClick}
        viewportDimensions={viewportDimensions}
        ref={loadMapIcons}
        infoPanelOpen={!!selected}
      />

      {selected && (
        <Info>
          {selected.type === "address" ?
            <AddressPanel
              address={selected.address}
              longitude={selected.longitude}
              latitude={selected.latitude}
              propertyId={selected.propertyId}
              parcelId={selected.parcelId}
              parcelNumber={selected.parcelNumber}
              hasPropertyIds={selected.hasPropertyIds}
              onClose={onAddressPanelClose}
            />
          : <ObjectInformationPanel
              customMapId={selected.customMapId}
              geometryId={selected.geometryId}
              fromSavedView={selected.fromSavedView}
              onClose={onAddressPanelClose}
            />
          }
        </Info>
      )}
    </Container>
  );
};
